import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FlipHorizontalIcon, FlipVerticalIcon } from "lucide-react";

interface Props {
  onClose: () => void;
  onConfirm: (direction: "horizontal" | "vertical", dimension: number) => void;
  nodeWidth: number;
  nodeHeight: number;
}

export const SplitContentManualSplitDialog: React.FC<Props> = ({ onClose, onConfirm, nodeWidth, nodeHeight }) => {
  const { t } = useTranslation();
  const [direction, setDirection] = useState<"horizontal" | "vertical">("horizontal");
  const [dimension, setDimension] = useState<string>("");

  const maxDimension = direction === "horizontal" ? nodeWidth : nodeHeight;
  const dimensionLabel = direction === "horizontal" ? "Width" : "Height";

  const handleConfirm = () => {
    const parsedDimension = parseInt(dimension);
    if (parsedDimension > 0 && parsedDimension < maxDimension) {
      onConfirm(direction, parsedDimension);
      onClose();
    }
  };

  const isValid = () => {
    return true;
    // TODO validations disabled temp
    // We need to compute the selected node size in their parent and check here basically
    // const parsedDimension = parseInt(dimension);
    // return parsedDimension > 0 && parsedDimension < maxDimension && !isNaN(parsedDimension);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("splitContent.splitManually")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="flex gap-2 mt-2">
              <Button
                variant={direction === "horizontal" ? "default" : "outline"}
                onClick={() => setDirection("horizontal")}
                className="flex-1"
              >
                <FlipHorizontalIcon className="w-4 h-4 mr-2" />
                {t("splitContent.splitVertically")}
              </Button>
              <Button
                variant={direction === "vertical" ? "default" : "outline"}
                onClick={() => setDirection("vertical")}
                className="flex-1"
              >
                <FlipVerticalIcon className="w-4 h-4 mr-2" />
                {t("splitContent.splitHorizontally")}
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="dimension">
              {dimensionLabel}
              {/* (px) - Max: {maxDimension}px */}
            </Label>
            <Input
              id="dimension"
              type="number"
              value={dimension}
              onChange={(e) => setDimension(e.target.value)}
              placeholder={`Enter ${dimensionLabel.toLowerCase()} in pixels`}
              min={1}
              max={maxDimension - 1}
              className="mt-1"
            />
            {dimension && !isValid() && (
              <p className="text-sm text-red-600 mt-1">Please enter a value between 1 and {maxDimension - 1}</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={!isValid()}>
            {t("common.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
